/**
 * GalleryManager Component
 * Staff UI for creating and managing client galleries within jobs
 * Supports linking albums, publish/unpublish, and creating selection requests
 */

import React, { useState, useEffect } from 'react';
import { FiImage, FiPlus, FiX } from 'react-icons/fi';
import GalleryCard from './GalleryCard';
import {
  GalleryModal,
  SelectionRequestModal,
  PublishOptionsModal,
  PublishSuccessModal,
  CreateTokenModal,
  FeedbackReviewModal
} from './GalleryModals';
import PhotoPickerModal from './PhotoPickerModal';

export default function GalleryManager({ jobId, customerId, customer, onGalleryChange, uploadsAlbumId = null, annotationStats = {} }) {
  const [galleries, setGalleries] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modal states
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [showSelectionModal, setShowSelectionModal] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(null);
  const [galleryImages, setGalleryImages] = useState({});
  const [publishResult, setPublishResult] = useState(null);
  const [showPublishOptions, setShowPublishOptions] = useState(null);
  const [showReview, setShowReview] = useState(null);
  const [favoritesImages, setFavoritesImages] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesError, setFavoritesError] = useState(null);
  const [replacingId, setReplacingId] = useState(null);

  useEffect(() => {
    loadData();
  }, [jobId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [galleriesRes, albumsRes] = await Promise.all([
        fetch(`/api/galleries?job_id=${jobId}`, { credentials: 'include' }),
        fetch('/api/albums', { credentials: 'include' })
      ]);

      if (!galleriesRes.ok) throw new Error('Failed to load galleries');
      if (!albumsRes.ok) throw new Error('Failed to load albums');

      const galleriesData = await galleriesRes.json();
      const albumsData = await albumsRes.json();

      setGalleries(galleriesData);
      setAlbums(albumsData.albums || albumsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewFeedback = async (gallery) => {
    if (!gallery) return;
    setShowReview(gallery);
    setFavoritesImages([]);
    setFavoritesError(null);
    setFavoritesLoading(true);
    try {
      const res = await fetch(`/api/galleries/${gallery.id}/favorites`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load favorites');
      const data = await res.json();
      setFavoritesImages(data.images || []);
    } catch (err) {
      setFavoritesError(err.message || 'Failed to load favorites');
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleReplaceFavorite = async (image, file) => {
    if (!image || !file) return;
    setReplacingId(image.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/images/${image.id}/replace`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (!res.ok) throw new Error('Replace failed');
      const data = await res.json();
      if (data?.image) {
        setFavoritesImages((prev) => prev.map((img) => img.id === image.id ? { ...img, ...data.image } : img));
      }
    } catch (err) {
      setFavoritesError(err.message || 'Failed to replace photo');
    } finally {
      setReplacingId(null);
    }
  };

  const handleSaveGallery = async (data) => {
    try {
      setSaving(true);
      const isEdit = !!editingGallery;

      const res = await fetch(
        isEdit ? `/api/galleries/${editingGallery.id}` : '/api/galleries',
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data)
        }
      );

      if (!res.ok) throw new Error('Failed to save gallery');

      await loadData();
      setShowGalleryModal(false);
      setEditingGallery(null);
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGallery = async (gallery) => {
    if (!confirm(`Delete "${gallery.title}"? This cannot be undone.`)) return;

    try {
      const res = await fetch(`/api/galleries/${gallery.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete gallery');

      await loadData();
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePublish = async (password = null, shareMode = 'proofs') => {
    const gallery = showPublishOptions;
    if (!gallery) return;

    try {
      setSaving(true);

      // Step 1: Publish the gallery
      const publishRes = await fetch(`/api/galleries/${gallery.id}/publish`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!publishRes.ok) throw new Error('Failed to publish gallery');

      // Step 2: Create share token with optional password
      const tokenRes = await fetch('/api/client-tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: 'gallery',
          gallery_id: gallery.id,
          customer_id: gallery.customer_id || customerId,
          job_id: gallery.job_id || jobId,
          password: password || null,
          allow_downloads: false,
          allow_comments: true,
          allow_favorites: true
        })
      });
      if (!tokenRes.ok) throw new Error('Failed to create share link');
      const tokenResult = await tokenRes.json();

      // Step 3: Only create selection request if in selection mode
      if (shareMode === 'selection') {
        const selectionRes = await fetch('/api/selection-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            gallery_id: gallery.id,
            job_id: gallery.job_id || jobId,
            customer_id: gallery.customer_id || customerId,
            title: `Selection Request - ${gallery.title}`,
            instructions: 'Please select your favorite photos from this gallery.',
            rules: {
              allowRanking: false,
              allowNotesPerAsset: false
            }
          })
        });
        if (!selectionRes.ok) throw new Error('Failed to create selection request');
      }

      setShowPublishOptions(null);
      setPublishResult({
        gallery,
        token: tokenResult.token,
        shareMode
      });

      await loadData();
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async (gallery) => {
    try {
      const res = await fetch(`/api/galleries/${gallery.id}/unpublish`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to unpublish gallery');

      await loadData();
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateSelectionRequest = async (data) => {
    try {
      setSaving(true);

      const res = await fetch('/api/selection-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...data,
          job_id: jobId,
          customer_id: customerId
        })
      });

      if (!res.ok) throw new Error('Failed to create selection request');

      setShowSelectionModal(null);
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateToken = async (data) => {
    try {
      setSaving(true);

      const res = await fetch('/api/client-tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!res.ok) throw new Error('Failed to create token');

      const result = await res.json();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    try {
      const res = await fetch(`/api/client-tokens/${tokenId}/revoke`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to revoke token');

      await loadData();
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddPhotos = async (imageIds) => {
    if (!showPhotoPicker || imageIds.length === 0) return;

    try {
      setSaving(true);
      const galleryId = showPhotoPicker.id;

      const res = await fetch(`/api/galleries/${galleryId}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_ids: imageIds })
      });

      if (!res.ok) throw new Error('Failed to add photos');

      await loadData();
      setShowPhotoPicker(null);
      onGalleryChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gallery-manager loading">
        <div className="spinner" />
        <span>Loading galleries...</span>
      </div>
    );
  }

  return (
    <div className="gallery-manager">
      {error && (
        <div className="gallery-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}><FiX /></button>
        </div>
      )}

      <div className="gallery-manager-header">
        <div>
          <h3>Client Galleries</h3>
          <p>Create and manage photo galleries for this job</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingGallery(null);
            setShowGalleryModal(true);
          }}
        >
          <FiPlus /> New Gallery
        </button>
      </div>

      {galleries.length === 0 ? (
        <div className="gallery-empty">
          <FiImage size={48} />
          <h4>No galleries yet</h4>
          <p>Create a gallery to share photos with your client</p>
          <button
            className="btn-primary"
            onClick={() => {
              setEditingGallery(null);
              setShowGalleryModal(true);
            }}
          >
            <FiPlus /> Create First Gallery
          </button>
        </div>
      ) : (
        <div className="gallery-grid">
          {galleries.map(gallery => (
            <GalleryCard
              key={gallery.id}
              gallery={gallery}
              onEdit={(g) => {
                setEditingGallery(g);
                setShowGalleryModal(true);
              }}
              onDelete={handleDeleteGallery}
              onPublish={(g) => setShowPublishOptions(g)}
              onUnpublish={handleUnpublish}
              onCreateToken={(g) => setShowTokenModal(g)}
              onCreateSelectionRequest={(g) => setShowSelectionModal(g)}
              onAddPhotos={(g) => setShowPhotoPicker(g)}
              onRevokeToken={handleRevokeToken}
              onReviewFeedback={handleReviewFeedback}
              pendingAnnotations={annotationStats[gallery.id]?.pending || 0}
            />
          ))}
        </div>
      )}

      {showGalleryModal && (
        <GalleryModal
          gallery={editingGallery}
          jobId={jobId}
          customerId={customerId}
          albums={albums}
          onSave={handleSaveGallery}
          onClose={() => {
            setShowGalleryModal(false);
            setEditingGallery(null);
          }}
          saving={saving}
        />
      )}

      {showSelectionModal && (
        <SelectionRequestModal
          gallery={showSelectionModal}
          onSave={handleCreateSelectionRequest}
          onClose={() => setShowSelectionModal(null)}
          saving={saving}
        />
      )}

      {showTokenModal && (
        <CreateTokenModal
          gallery={showTokenModal}
          customer={customer}
          onSave={handleCreateToken}
          onClose={() => {
            setShowTokenModal(null);
            loadData();
          }}
          saving={saving}
        />
      )}

      {showReview && (
        <FeedbackReviewModal
          gallery={showReview}
          images={favoritesImages}
          loading={favoritesLoading}
          error={favoritesError}
          replacingId={replacingId}
          onClose={() => {
            setShowReview(null);
            setFavoritesImages([]);
            setFavoritesError(null);
          }}
          onReplace={handleReplaceFavorite}
        />
      )}

      {showPhotoPicker && (
        <PhotoPickerModal
          isOpen={true}
          onClose={() => setShowPhotoPicker(null)}
          onSelect={handleAddPhotos}
          excludeIds={galleryImages[showPhotoPicker.id] || []}
          title={`Add Photos to ${showPhotoPicker.title}`}
          multiSelect={true}
          albumId={uploadsAlbumId}
        />
      )}

      {showPublishOptions && (
        <PublishOptionsModal
          gallery={showPublishOptions}
          onPublish={handlePublish}
          onClose={() => setShowPublishOptions(null)}
          publishing={saving}
        />
      )}

      {publishResult && (
        <PublishSuccessModal
          gallery={publishResult.gallery}
          token={publishResult.token}
          shareMode={publishResult.shareMode}
          onClose={() => setPublishResult(null)}
        />
      )}
    </div>
  );
}
